import { normalizeBlur } from '@signa/shared';
import { byOrder } from './layout.js';
/**
 * 書き出し用の変換を作る。
 *
 * **キャンバス寸法をそのまま使う**(ADR-0036 決定 5)。
 * [ADR-0033](../../../docs/adr/0033-canvas-aspect-from-background.md) により
 * `canvas` は**背景の最適化版の寸法**(長辺 1920)なので、**上限を別に設ける必要がない。**
 *
 * **表示用の寸法は使えない。** あれは入れ物のサイズと DPR 上限で決まる(#174)ため、
 * **同じプロジェクトでも端末ごとに変わってしまう。**
 *
 * 寸法が取れないときは `null`。**呼び出し側が「書き出せない」と判断する材料**になる。
 *
 * **`sizing.even` なら縦横を偶数に切り下げる**(#356 —— [`ComposeSizing`](#ComposeSizing))。
 */
export function exportTransform(canvas, sizing = {}) {
    const width = Math.round(canvas.width);
    const height = Math.round(canvas.height);
    // 未設定・壊れたデータを描こうとしない。canvas は 0 を許さないはずだが、
    // **描画コアは公開 API を知らない**ので、渡ってきた値を信用しきらない。
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
    }
    if (sizing.even === true) {
        return { width: evenDown(width), height: evenDown(height), offsetX: 0, offsetY: 0 };
    }
    return { width, height, offsetX: 0, offsetY: 0 };
}
/** 偶数に切り下げる。**最小 2**(0 の寸法は描けない)。 */
export function evenDown(value) {
    return Math.max(2, Math.floor(value / 2) * 2);
}
/**
 * 障害物 1 つの描き方を決める。
 *
 * **ぼかし 0 は `clip`。** 表示側も同じで、**ラスタライズが要るマスク画像の
 * コストを払うのは実際にぼかす障害物だけ**にしている([ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md))。
 *
 * `stdDeviation` は**キャンバス幅比 × 出力幅**。表示の [`maskStyle`](./stage.ts) が
 * `blur * transform.width` で出しているのと**同じ式**である ——
 * 式が違うと、書き出した絵のぼけ方が画面と変わる。
 */
export function obstacleMask(obstacle, transform) {
    const blur = normalizeBlur(obstacle.blur);
    const stdDeviation = blur * transform.width;
    if (stdDeviation <= 0) {
        return { kind: 'clip', id: obstacle.id, points: obstacle.points };
    }
    return { kind: 'blur', id: obstacle.id, points: obstacle.points, stdDeviation };
}
/**
 * テンプレートから書き出し 1 枚分の計画を組む。
 *
 * 寸法が取れなければ `null`(書き出せない)。
 *
 * **背景が無ければ障害物マスクも無い。** マスクは「背景画像を障害物の形に切り抜いて
 * 手前に重ねる」方式なので、**背景が無いと切り抜く絵が無い** ——
 * 表示側([`renderMasks`](./stage.ts))も同じ条件で何も描かない。
 * **ここを揃えないと、背景未設定のプロジェクトで画面と書き出しが食い違う。**
 */
export function composePlan(template, sizing = {}) {
    // **寸法・変換・マスクのぼかしは同じ `transform` から出す** —— 偶数にしたときも食い違わない(#356)。
    const transform = exportTransform(template.canvas, sizing);
    if (transform === null)
        return null;
    const masks = template.backgroundUrl === null
        ? []
        : byOrder(template.obstacles).map((o) => obstacleMask(o, transform));
    return {
        size: { width: transform.width, height: transform.height },
        transform,
        frames: byOrder(template.videoFrames),
        masks,
    };
}
//# sourceMappingURL=compose-plan.js.map